import type { FC } from 'react';
import { useCallback, useContext, useMemo, useState, memo, useEffect } from 'react';
import { Save, Close } from '@mui/icons-material';
import { capitalize } from '@trinity/chat-components/utils';
import { requestMicrophonePermissionIfNeeded } from '@trinity/chat-components/utils';
import type { ChatAction } from '@trinity/chat';
import { ChatActionType } from '@trinity/chat';
import { useSpeechRecognition } from '../Chat/hooks/useSpeechRecognition';
import type { apiSchemas } from '@trinity/management-app-client';
import type { AIFormState } from '@trinity/chat';

type FormSchema = apiSchemas.FormSchema;
import DynamicForm from './DynamicForm.form';
import { DynamicFormIntroduction } from './DynamicForm.introduction';
import { DynamicFormProbing } from './DynamicForm.probing';
import { SystemUserPlaceholderMessage } from '../Chat/Message.System.Placeholder';
import {
  ActionButtonContainer,
  ActionButton,
  Message,
} from './DynamicForm.styles.index';
import { ActiveChatSessionContext, SubmitActionContext } from '../Chat/context';
import { DynamicFormDashboard } from './DynamicForm.dashboard';

const CONSTANTS = {
  INSIGHT_MIN_LENGTH: 10,
  INSIGHT_MEANINGFUL_LENGTH: 20,
  ERROR_MESSAGES: {
    REQUIRED_FIELDS: 'Please fill in all required fields.',
    INSIGHT_TOO_SHORT: 'Please provide a more detailed insight (minimum 10 characters).',
    SUBMIT_FAILED: 'Failed to save interaction. Please try again.',
    GENERIC_ERROR: 'An unexpected error occurred. Please try again.',
  },
  MESSAGES: {
    SAVING: 'Saving...',
    SAVE: 'Save',
    SAVE_AS_DRAFT: 'Save as Draft',
    CANCEL: 'Cancel',
    SUBMITTED: 'Submitted',
  },
} as const;

type Step = 'initial' | 'probing' | 'manual-entry' | 'draft' | 'submitted' | 'cancelled';

const getTodaysDate = (): string => {
  return new Date().toISOString().split('T')[0];
};

const validateDynamicFormData = (data: Record<string, unknown>, config: FormSchema | undefined): boolean => {
  if (!config || !config.fields) {
    return true;
  }
  const requiredFields = config.fields.filter(field => field.required);
  const isValid = requiredFields.every(field => {
    const value = data[field.name];
    if (!value) {
      return false;
    }
    if (typeof value === 'string' && value.trim() === '') {
      return false;
    }
    if (Array.isArray(value) && value.length === 0) {
      return false;
    }
    if (field.name === 'insight' && typeof value === 'string') {
      const isLongEnough = value.trim().length >= CONSTANTS.INSIGHT_MIN_LENGTH;
      return isLongEnough;
    }
    return true;
  });
  return isValid;
};

interface Props {
  queryId: number;
  queryIdentifier?: string;
  value: Record<string, unknown> & {
    formState?: Step;
    showButtons?: boolean;
    isFromSpeech?: boolean;
    formConfig?: FormSchema;
    aiState?: AIFormState;
    submittedData?: {
      formData?: Array<{
        fieldLabel: string;
        fieldType: string;
        value: unknown;
      }>;
    };
    submittedAt?: string | number | Date;
    draftAt?: string | number | Date;
    availableDrafts?: Array<{
      id: string;
      lastUpdated: string;
      preview: Record<string, unknown>;
    }>;
    draftOnlyMode?: boolean; // When true, only show draft options, hide new entry buttons
  };
  workflowType?: string;
  actionType?: string;
  introConfig?: {
    prompt?: string;
    warningMessage?: string;
    showSpeechOption?: boolean;
    showManualButton?: boolean;
    showCancelButton?: boolean;
    introResponse?: { value: string; attachments?: unknown[] };
  };
}

const DynamicFormWizardContent: FC<Props> = memo((props) => {
  const submitAction = useContext(SubmitActionContext);
  const chatSession = useContext(ActiveChatSessionContext);

  const formConfig = useMemo(() => {
    return props.value.formConfig;
  }, [props.value.formConfig]);

  const valueWithState = useMemo(() => ({
    ...props.value,
    formState: props.value.formState ?? 'initial' as Step,
    showButtons: props.value.showButtons ?? true,
  }), [props.value]);

  const [currentStep, setCurrentStep] = useState<Step>(valueWithState.formState);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [isCancelled, setIsCancelled] = useState(false);
  const [aiState, setAiState] = useState<AIFormState | null>(null);
  const [correctionsApplied, setCorrectionsApplied] = useState(false);
  const [isListeningInInitial, setIsListeningInInitial] = useState(false);
  const [isListeningInProbing, setIsListeningInProbing] = useState(false);
  const [isEditingDraft, setIsEditingDraft] = useState(false);
  useEffect(() => {
    const newFormState = props.value.formState ?? 'initial';
    const status = props.value.status;

    // If draftOnlyMode is true and availableDrafts exist, show initial state (draft list)
    // This ensures the draft list persists after reload, even if formState is 'draft'
    const shouldShowDraftList = props.value.draftOnlyMode === true &&
      props.value.availableDrafts &&
      Array.isArray(props.value.availableDrafts) &&
      props.value.availableDrafts.length > 0;

    if (status === 'form-completion' || newFormState === 'submitted') {
      setCurrentStep('submitted');
      setIsEditingDraft(false);
    } else if (shouldShowDraftList) {
      // Show draft list if in draft-only mode with available drafts
      setCurrentStep('initial');
      setIsEditingDraft(false);
    } else if (currentStep !== 'submitted' && currentStep !== 'cancelled' && !isCancelled) {
      // Don't override currentStep if user is actively editing a draft
      if (!isEditingDraft && newFormState && newFormState !== currentStep && newFormState !== 'initial') {
        setCurrentStep(newFormState);
      }
    }
  }, [props.value.formState, props.value.status, props.value.draftOnlyMode, props.value.availableDrafts, currentStep, props.value.submittedData, isSubmitting, isCancelled, isEditingDraft]);
  
  // Reset isSubmitting when backend response changes the form state
  useEffect(() => {
    if (isSubmitting && props.value.formState && props.value.formState !== 'probing') {
      setIsSubmitting(false);
    }
  }, [props.value.formState, isSubmitting, currentStep]);
  
  useEffect(() => {
    if (props.value.aiState) {
      setAiState(props.value.aiState);
      setCorrectionsApplied(false);
    } else {
      setAiState(null);
      setCorrectionsApplied(false);
    }
  }, [props.value.aiState]);
  const initialFormData = useMemo(() => {
    const data: Record<string, unknown> = {};

    if (formConfig?.fields) {
      formConfig.fields.forEach(field => {
        // First try to get value by field name
        let value = props.value[field.name];
        
        // If not found, try to find by field label (for backwards compatibility with drafts saved with label keys)
        if (value === undefined && props.value[field.label]) {
          value = props.value[field.label];
        }
        
        data[field.name] = value || field.defaultValue;
      });
    }

    if (!data.date) {
      data.date = getTodaysDate();
    }

    const existingInsight = props.value.insight as string || '';
    const shouldUseExistingInsight = existingInsight &&
      existingInsight.length > CONSTANTS.INSIGHT_MEANINGFUL_LENGTH &&
      !existingInsight.toLowerCase().includes('log an interaction');

    if (shouldUseExistingInsight) {
      data.insight = existingInsight;
    }

    return data;
  }, [props.value, formConfig]);
  const getFormDataWithOriginalValues = useCallback(() => {
    if (aiState?.originalExtraction && aiState?.patternInsights?.some(p => p.hasCorrectionHistory)) {
      return { ...initialFormData, ...aiState.originalExtraction };
    }
    return initialFormData;
  }, [initialFormData, aiState]);

  const [dynamicFormData, setDynamicFormData] = useState<Record<string, unknown>>(() => {
    return getFormDataWithOriginalValues();
  });

  useEffect(() => {
    if (currentStep === 'initial') {
      let dataToUse = initialFormData;
      if (aiState?.originalExtraction && aiState?.patternInsights?.some(p => p.hasCorrectionHistory)) {
        dataToUse = { ...initialFormData, ...aiState.originalExtraction };
      }
      setDynamicFormData(dataToUse);
    }
  }, [initialFormData, currentStep, aiState]);
  const submittedFormData = useMemo(() => {
    if (currentStep === 'submitted' && props.value.submittedData?.formData && formConfig?.fields) {
      const data: Record<string, unknown> = {};
      const submittedData = props.value.submittedData.formData;

      submittedData.forEach(item => {
        const field = formConfig.fields.find(f => f.label === item.fieldLabel);
        if (field) {
          data[field.name] = item.value;
        }
      });

      return data;
    }
    return null;
  }, [currentStep, props.value.submittedData, formConfig]);

  const formDataToDisplay = submittedFormData || dynamicFormData;
  const isValid = useMemo(() => {
    return validateDynamicFormData(dynamicFormData, formConfig);
  }, [dynamicFormData, formConfig]);

  // Speech recognition handlers - updates insight field directly
  const onSpeechFinished = useCallback((value: string, prefix: string) => {
    // Handle undefined or empty prefix to prevent "undefined" appearing in transcript
    const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
    let newVal = safePrefix ? `${safePrefix} ${capitalize(value)}` : capitalize(value);

    // Add punctuation if missing
    if (!endsWithPunctuation(newVal)) {
      newVal = newVal + '.';
    }

    setDynamicFormData(prev => ({
      ...prev,
      insight: newVal,
    }));

    // After speech finishes in initial step, transition to manual-entry
    if (currentStep === 'initial' && isListeningInInitial) {
      setIsListeningInInitial(false);
    }
    
    // After speech finishes in probing step, just stop listening
    // User will manually submit via chat input like in initial step
    if (currentStep === 'probing' && isListeningInProbing) {
      setIsListeningInProbing(false);
    }
  }, [currentStep, isListeningInInitial, isListeningInProbing]);

  const onInterimResult = useCallback((result: string, prefix: string) => {
    // Handle undefined or empty prefix to prevent "undefined" appearing in transcript
    const safePrefix = prefix && prefix.trim() ? prefix.trim() : '';
    const value = safePrefix ? `${safePrefix} ${capitalize(result)}` : capitalize(result);

    setDynamicFormData(prev => ({
      ...prev,
      insight: value,
    }));
  }, []);

  const onAudioLevelChange = useCallback((_level: number) => {
    // Audio level monitoring for future use
  }, []);

  const { start, stop, listening } = useSpeechRecognition({
    continuous: true,
    onResult: onSpeechFinished,
    onInterimResult,
    onAudioLevelChange,
  });

  // Cleanup effect when component unmounts
  useEffect(() => {
    return () => {
      if (listening) {
        stop();
      }
    };
  }, [listening, stop]);

  const handleFieldChange = useCallback((fieldName: string, value: unknown) => {
    setDynamicFormData(prev => {
      const newData = { ...prev, [fieldName]: value };
      return newData;
    });
  }, []);
  const handleAICorrection = useCallback((fieldName: string, _originalValue: unknown, correctedValue: unknown) => {
    setDynamicFormData(prev => ({
      ...prev,
      [fieldName]: correctedValue,
    }));

    setCorrectionsApplied(true);
  }, []);
  const onSubmit = useCallback(async () => {
    if (!isValid || !submitAction) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setIsEditingDraft(false);
    const previousStep = currentStep;
    setCurrentStep('submitted');
    try {
      const filteredFormData = formConfig.fields.map(field => ({
        fieldLabel: field.label,
        fieldType: field.type,
        value: dynamicFormData[field.name] || '',
      })).filter(item => item.value !== null && item.value !== undefined && item.value !== '');

      const workflowType = props.workflowType;

      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType: workflowType,
          formParentId: formConfig.parentId,
          formVersion: formConfig.version,
          formState: 'submitted',
          formData: filteredFormData,
          aiState: aiState,
        },
      };

      await submitAction(action, 'Submit');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : CONSTANTS.ERROR_MESSAGES.SUBMIT_FAILED);
      setCurrentStep(previousStep);
    } finally {
      setIsSubmitting(false);
    }
  }, [isValid, submitAction, dynamicFormData, props.queryId, props.workflowType, formConfig, aiState, currentStep]);

  const onDraft = useCallback(async () => {
    if (!submitAction) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    setIsEditingDraft(false);
    const previousStep = currentStep;
    setCurrentStep('draft');
    try {
      const filteredFormData = formConfig.fields.map(field => ({
        fieldLabel: field.label,
        fieldType: field.type,
        value: dynamicFormData[field.name] || '',
      })).filter(item => item.value !== null && item.value !== undefined && item.value !== '');

      const workflowType = props.workflowType;

      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType: workflowType,
          formParentId: formConfig.parentId,
          formVersion: formConfig.version,
          formState: 'draft',
          formData: filteredFormData,
          aiState: aiState,
        },
      };

      await submitAction(action, 'Save Draft');
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : CONSTANTS.ERROR_MESSAGES.SUBMIT_FAILED);
      setCurrentStep(previousStep);
    } finally {
      setIsSubmitting(false);
    }
  }, [submitAction, dynamicFormData, props.queryId, props.workflowType, formConfig, aiState, currentStep]);

  const onEdit = useCallback(() => {
    setIsEditingDraft(true);
    setCurrentStep('manual-entry');
  }, []);

  const handleStartManualEntry = useCallback(() => {
    setCurrentStep('manual-entry');
  }, []);

  // Voice entry toggle - matches InputControl.Container pattern exactly
  const handleStartSpeechEntry = useCallback(() => {
    if (listening) {
      // If already listening, stop
      stop();
      setIsListeningInInitial(false);
    } else {
      // Request permission and start listening
      requestMicrophonePermissionIfNeeded().then(granted => {
        if (granted) {
          // Stay in initial mode but hide buttons and show listening state
          setIsListeningInInitial(true);

          // Ensure we don't pass undefined as prefix
          const currentInsight = dynamicFormData.insight || '';
          const safeInput = currentInsight && typeof currentInsight === 'string' ? currentInsight : '';
          start(safeInput);
        }
      });
    }
  }, [listening, start, stop, dynamicFormData.insight]);

  // Probing step handlers
  const handleProbingManualEntry = useCallback(async () => {
    // Transition to manual-entry by submitting to backend
    // This will trigger the proper response message and form state
    if (!submitAction) {
      return;
    }
    
    setIsSubmitting(true);
    
    try {
      // Submit form state update to transition from probing to manual-entry
      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType: props.workflowType,
          formState: 'manual-entry',
          formData: [],
          aiState,
        },
      };
      await submitAction(action, 'Continue to form');
      // Backend will respond with manual-entry state and the "I've analyzed your text" message
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to load form');
      setIsSubmitting(false);
    }
  }, [submitAction, props.queryId, props.workflowType, aiState]);

  const handleProbingSpeechEntry = useCallback(async () => {
    if (listening) {
      stop();
      setIsListeningInProbing(false);
    } else {
      const granted = await requestMicrophonePermissionIfNeeded();
      if (granted) {
        setIsListeningInProbing(true);
        const currentInsight = dynamicFormData.insight || '';
        const safeInput = currentInsight && typeof currentInsight === 'string' ? currentInsight : '';
        start(safeInput);
      }
    }
  }, [listening, start, stop, dynamicFormData.insight]);

  const handleProbingRetry = useCallback(async (newTranscript: string) => {
    if (!submitAction || !aiState) {
      return;
    }

    try {
      setIsSubmitting(true);
      
      // Combine the original transcript with the new one
      const combinedTranscript = aiState.combinedTranscript 
        ? `${aiState.combinedTranscript} ${newTranscript}`
        : newTranscript;
      
      // Don't increment retry count here - backend will handle it
      // Update aiState with combined transcript only
      const updatedAiState: AIFormState = {
        ...aiState,
        combinedTranscript,
      };
      
      setAiState(updatedAiState);
      
      // Update the form data with combined transcript
      setDynamicFormData(prev => ({
        ...prev,
        insight: combinedTranscript,
      }));
      
      // Submit to backend for re-extraction
      // Backend will create a user message with the new transcript portion
      const action: ChatAction = {
        type: ChatActionType.SubmitFormWorkflow,
        payload: {
          queryId: props.queryId,
          workflowType: props.workflowType,
          formState: 'probing',
          formData: [{
            fieldLabel: 'insight',
            value: combinedTranscript,
          }],
          aiState: updatedAiState,
        },
      };
      
      await submitAction(action, 'Retry extraction');
      
      // Backend will respond with either 'probing' (more missing fields) or 'manual-entry' (all filled)
      // The response will update the state automatically via props
      
    } catch (error) {
      setSubmitError(error instanceof Error ? error.message : 'Failed to process voice input');
    } finally {
      setIsSubmitting(false);
    }
  }, [submitAction, aiState, props.queryId, props.workflowType, chatSession]);

  const handleCancel = useCallback(async () => {
    try {
      setIsCancelled(true);
      setSubmitError(null);
      setIsSubmitting(false);
      setDynamicFormData(initialFormData);
      setCurrentStep('cancelled');

      if (submitAction) {
        const workflowType = props.workflowType;

        const action: ChatAction = {
          type: ChatActionType.SubmitFormWorkflow,
          payload: {
            queryId: props.queryId,
            workflowType: workflowType,
            formState: 'cancelled',
            formData: [],
          },
        };
        await submitAction(action, 'Cancel');
      }
    } catch {
      setIsCancelled(true);
      setCurrentStep('cancelled');
    }
  }, [submitAction, props.queryId, props.workflowType, initialFormData]);

  const handleLoadDraft = useCallback(async (draftId: string) => {
    if (!submitAction) {
      console.error('Submit action not available');
      return;
    }

    if (isSubmitting) {
      // Prevent multiple simultaneous loads
      return;
    }

    setIsSubmitting(true);
    setSubmitError(null);

    try {
      const action: ChatAction = {
        type: ChatActionType.LoadDraft,
        payload: {
          queryId: props.queryId,
          draftId: draftId,
        },
      };

      await submitAction(action, 'Load Draft');
    } catch (error) {
      console.error('Error loading draft:', error);
      setSubmitError(error instanceof Error ? error.message : 'Failed to load draft');
      // Don't set isSubmitting to false on error so user can see the error state
    } finally {
      // Only reset submitting state after a short delay to prevent rapid clicks
      setTimeout(() => {
        setIsSubmitting(false);
      }, 500);
    }
  }, [submitAction, props.queryId, isSubmitting]);

  const showButtons = valueWithState.showButtons && !isCancelled;
  if (!formConfig) {
    return null;
  }
  switch (currentStep) {
    case 'initial':
      return (
        <>
          {/* {formConfig.richContentEndpoint && (
            <DynamicFormRichContentPreview
              endpoint={formConfig.richContentEndpoint}
              variant="initial"
            />
          )} */}
          <DynamicFormIntroduction
            onManualEntry={handleStartManualEntry}
            onSpeechEntry={handleStartSpeechEntry}
            onCancel={handleCancel}
            onLoadDraft={handleLoadDraft}
            prompt={props.introConfig?.prompt || formConfig?.prompt?.initialMessage || 'Please fill out the form'}
            warningMessage={props.introConfig?.warningMessage}
            showCancelButton={isListeningInInitial ? false : (props.introConfig?.showCancelButton ?? showButtons)}
            showSpeechOption={isListeningInInitial ? false : (props.introConfig?.showSpeechOption ?? showButtons)}
            showManualButton={isListeningInInitial ? false : (props.introConfig?.showManualButton ?? showButtons)}
            showWarning={!isListeningInInitial}
            isVoiceActive={isListeningInInitial || listening}
            availableDrafts={props.value.availableDrafts}
            draftOnlyMode={props.value.draftOnlyMode}
            introResponse={props.introConfig?.introResponse}
          />
        </>
      );

    case 'probing':
      return (
        <>
          <DynamicFormProbing
            onManualEntry={handleProbingManualEntry}
            onSpeechEntry={handleProbingSpeechEntry}
            onCancel={handleCancel}
            missingRequiredFields={aiState?.missingRequiredFields || []}
            retryCount={aiState?.retryCount ?? 0}
            maxRetries={aiState?.maxRetries ?? 3}
            showCancelButton={!isListeningInProbing && showButtons && !isSubmitting}
            showSpeechOption={!isListeningInProbing && (props.introConfig?.showSpeechOption ?? showButtons) && !isSubmitting}
            showManualButton={!isListeningInProbing && (props.introConfig?.showManualButton ?? showButtons) && !isSubmitting}
            isVoiceActive={isListeningInProbing || listening}
          />
        </>
      );

    case 'manual-entry':
      return (
        <>
          <DynamicForm
            formConfig={formConfig}
            formData={dynamicFormData}
            onFieldChange={handleFieldChange}
            isValid={isValid}
            isSubmitting={isSubmitting}
            isSubmitted={false}
            submitError={submitError}
            hasPrefilledData={Object.keys(props.value).length > 1}
            aiState={aiState}
            onAICorrection={handleAICorrection}
            correctionsApplied={correctionsApplied}
            showButtons={false} // Buttons are handled outside the form
            onDraft={onDraft}
          />

          <ActionButtonContainer>
            <ActionButton
              onClick={onSubmit}
              variant='primary'
              disabled={isSubmitting || !isValid}
              icon={Save}
            >
              {isSubmitting ? CONSTANTS.MESSAGES.SAVING : CONSTANTS.MESSAGES.SAVE}
            </ActionButton>
            <ActionButton
              onClick={onDraft}
              variant='primary'
              disabled={isSubmitting}
              type="button"
              icon={Save}
            >
              {isSubmitting ? CONSTANTS.MESSAGES.SAVING : CONSTANTS.MESSAGES.SAVE_AS_DRAFT}
            </ActionButton>

            <ActionButton
              variant='secondary'
              onClick={handleCancel}
              disabled={isSubmitting}
              icon={Close}
            >
              {CONSTANTS.MESSAGES.CANCEL}
            </ActionButton>
          </ActionButtonContainer>
        </>
      );

    case 'draft':
      return (
        <>
          <DynamicForm
            formConfig={formConfig}
            formData={formDataToDisplay}
            onFieldChange={handleFieldChange}
            isValid={isValid}
            isSubmitting={isSubmitting}
            isDraft={true}
            submitError={submitError}
            hasPrefilledData={Object.keys(props.value).length > 1}
            aiState={aiState}
            onAICorrection={handleAICorrection}
            correctionsApplied={correctionsApplied}
            showButtons={true}
            onEdit={onEdit}
            draftAt={props.value.draftAt}
          />
        </>
      );

    case 'submitted':
      return (
        <>
          <DynamicForm
            formConfig={formConfig}
            formData={formDataToDisplay}
            onFieldChange={handleFieldChange}
            isValid={isValid}
            isSubmitting={isSubmitting}
            isSubmitted={true}
            submitError={submitError}
            hasPrefilledData={Object.keys(props.value).length > 1}
            aiState={aiState}
            onAICorrection={handleAICorrection}
            correctionsApplied={correctionsApplied}
            showButtons={false} // Submitted view is read-only
            submittedAt={props.value.submittedAt}
          />
        </>
      );

    case 'cancelled':
      return (
        <DynamicFormIntroduction
          onManualEntry={handleStartManualEntry}
          onSpeechEntry={handleStartSpeechEntry}
          onCancel={handleCancel}
          prompt={props.introConfig?.prompt || formConfig?.prompt?.initialMessage || 'Please fill out the form'}
          warningMessage={props.introConfig?.warningMessage}
          showCancelButton={false}
          showSpeechOption={false}
          showManualButton={false}
        />
      );
  }
});

export const DynamicFormWizard: FC<Props> = (props) => {
  const chatSession = useContext(ActiveChatSessionContext);

  if (!chatSession) {
    return null;
  }

  return <DynamicFormWizardContent {...props} />;
};

// Helper function to check if string ends with punctuation
function endsWithPunctuation(str: string): boolean {
  const punctuation = /[.,!?;:'"-]/;
  return punctuation.test(str.charAt(str.length - 1));
}

export default DynamicFormWizard;
